import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X, Sparkles, Loader2 } from "lucide-react";
import { useSubscription, type PlanTier } from "@/hooks/use-subscription";
import { useOnboarding } from "@/hooks/use-onboarding";
import { supabase } from "@/integrations/supabase/client";

interface PlanFeature {
  id: string;
  plan_id: string;
  feature_key: string;
  enabled: boolean;
  limit_value: number | null;
  created_at: string;
}

interface Plan {
  id: string;
  tier: string;
  name: string;
  description: string | null;
  price_cents: number;
  currency: string;
  trial_days: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  plan_features: PlanFeature[];
}

const FEATURE_LABELS: Record<string, string> = {
  max_checking_accounts: "Contas correntes",
  max_savings_accounts: "Contas poupança",
  max_credit_cards: "Cartões de crédito",
  credit_card_access: "Acesso a cartões",
  transfers_access: "Transferências",
  statement_import: "Importação de extratos",
  budget_planning: "Planejamento orçamentário",
  provisions: "Provisionamento",
  max_goals: "Cofrinhos",
  account_sharing: "Compartilhamento",
  max_shared_users: "Usuários compartilhados",
  dre_reports: "Relatórios DRE",
  ai_spending_analysis: "Análise inteligente",
  recurring_transactions: "Transações recorrentes",
  reports_access: "Relatórios",
};

const FEATURE_ORDER = [
  "max_checking_accounts",
  "max_savings_accounts",
  "max_credit_cards",
  "credit_card_access",
  "statement_import",
  "budget_planning",
  "provisions",
  "max_goals",
  "account_sharing",
  "max_shared_users",
  "dre_reports",
  "ai_spending_analysis",
  "recurring_transactions",
  "reports_access",
];

const FEATURES_TO_HIDE = [
  "max_savings_accounts",
  "max_credit_cards",
  "transfers_access",
  "statement_import",
  "provisions",
  "max_goals",
];

const TIER_ORDER: Record<string, number> = { freemium: 0, starter: 1, colab: 2 };

function formatPrice(priceCents: number): string {
  const reais = priceCents / 100;
  return reais.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });
}

function formatFeatureValue(feature: PlanFeature): React.ReactNode {
  if (!feature.enabled) {
    return <X className="h-4 w-4 text-muted-foreground/50" />;
  }
  if (feature.limit_value === null) {
    if (feature.feature_key.startsWith("max_")) {
      return <span className="text-xs font-medium text-primary">Ilimitado</span>;
    }
    return <Check className="h-4 w-4 text-primary" />;
  }
  if (feature.limit_value === 0) {
    return <X className="h-4 w-4 text-muted-foreground/50" />;
  }
  return <span className="text-xs font-medium text-foreground">{feature.limit_value}</span>;
}

interface UpgradeModalProps {
  externalOpen?: boolean;
  onExternalClose?: () => void;
}

export function UpgradeModal({ externalOpen, onExternalClose }: UpgradeModalProps = {}) {
  const { isFreemium, openCheckout } = useSubscription();
  const { onboarding, completeStep } = useOnboarding();
  const [autoOpen, setAutoOpen] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  // Track whether the tour was already completed when this component first loaded.
  // This prevents the modal from opening in the same session as onboarding/tour.
  const tourWasCompleted = useRef<boolean | null>(null);

  const open = externalOpen || autoOpen;

  useEffect(() => {
    if (!onboarding) return;

    // Capture the initial tour state on first load
    if (tourWasCompleted.current === null) {
      tourWasCompleted.current = !!onboarding.has_completed_tour;
    }

    if (
      isFreemium &&
      !onboarding.has_seen_upgrade_modal &&
      onboarding.has_completed_onboarding &&
      onboarding.has_completed_tour &&
      tourWasCompleted.current // only if tour was already done BEFORE this session
    ) {
      setAutoOpen(true);
    }
  }, [isFreemium, onboarding]);

  useEffect(() => {
    if (open && plans.length === 0) {
      fetchPlans();
    }
  }, [open]);

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const { data, error } = await supabase
        .from("plans" as any)
        .select("*, plan_features(*)")
        .eq("is_active", true)
        .order("price_cents", { ascending: true });

      if (error) return;

      const sorted = (data as unknown as Plan[]).sort(
        (a, b) => (TIER_ORDER[a.tier] ?? 99) - (TIER_ORDER[b.tier] ?? 99)
      );

      const filtered = sorted.map((plan) => ({
        ...plan,
        plan_features: plan.plan_features.filter(
          (f) => !FEATURES_TO_HIDE.includes(f.feature_key)
        ),
      }));

      setPlans(filtered);
    } finally {
      setPlansLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (externalOpen) {
      onExternalClose?.();
    } else {
      setAutoOpen(false);
      await completeStep("has_seen_upgrade_modal");
    }
  };

  const handleUpgrade = async (tier: PlanTier) => {
    if (!externalOpen) {
      await completeStep("has_seen_upgrade_modal");
    }
    onExternalClose?.();
    openCheckout(tier);
  };

  return (
    <Dialog open={open} onOpenChange={(value) => {
      if (!value) handleDismiss();
    }}>
      <DialogContent className="sm:max-w-3xl max-h-[90dvh] flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center">
            Conheça os planos do Next AI Finance
          </DialogTitle>
          <DialogDescription className="text-center">
            Escolha o plano ideal para suas necessidades financeiras.
          </DialogDescription>
        </DialogHeader>

        {plansLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4 overflow-y-auto flex-1 min-h-0">
            {plans.map((plan) => {
              const isPopular = plan.tier === "starter";
              const isFree = plan.price_cents === 0;
              const featureMap = new Map(
                plan.plan_features.map((f) => [f.feature_key, f])
              );

              return (
                <Card
                  key={plan.id}
                  className={`relative flex flex-col ${
                    isPopular
                      ? "border-primary shadow-lg ring-2 ring-primary/20"
                      : "border-border"
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="px-3 py-0.5 text-xs">Mais Popular</Badge>
                    </div>
                  )}

                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    {isFree ? (
                      <p className="text-2xl font-bold">Grátis</p>
                    ) : (
                      <p className="text-2xl font-bold">
                        {formatPrice(plan.price_cents)}
                        <span className="text-sm font-normal text-muted-foreground">/mês</span>
                      </p>
                    )}
                    {plan.description && (
                      <p className="text-xs text-muted-foreground">
                        {plan.description}
                      </p>
                    )}
                    {!isFree && plan.trial_days > 0 && (
                      <Badge variant="secondary" className="w-fit text-xs mt-1">
                        {plan.trial_days} dias grátis
                      </Badge>
                    )}
                    {isFree && (
                      <Badge variant="default" className="mt-2">
                        Pra sempre grátis
                      </Badge>
                    )}
                  </CardHeader>

                  <CardContent className="space-y-2 flex-1">
                    {FEATURE_ORDER.map((featureKey) => {
                      const feature = featureMap.get(featureKey);
                      if (!feature) return null;

                      return (
                        <div
                          key={featureKey}
                          className="flex items-center justify-between text-xs"
                        >
                          <span
                            className={
                              feature.enabled
                                ? "text-foreground"
                                : "text-muted-foreground/50"
                            }
                          >
                            {FEATURE_LABELS[featureKey] ?? featureKey}
                          </span>
                          {formatFeatureValue(feature)}
                        </div>
                      );
                    })}
                  </CardContent>

                  <CardFooter className="pt-3">
                    {isFree ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={handleDismiss}
                      >
                        Plano atual
                      </Button>
                    ) : (
                      <Button
                        variant={isPopular ? "default" : "outline"}
                        size="sm"
                        className="w-full gap-2"
                        onClick={() => handleUpgrade(plan.tier as PlanTier)}
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        Experimentar Grátis
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={handleDismiss} className="w-full sm:w-auto">
            Talvez depois
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
